import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, output } from '@angular/core';

import { IconComponent } from '../../common/icon/icon.component';
import { DataTableGeometryComponent } from '../data-table-geometry/data-table-geometry.component';
import { DataTableRowComponent } from '../data-table-row/data-table-row.component';
import type {
  DataTableCellContext,
  DataTableColumn,
  DataTableColumnGeometryChange,
  DataTableRow,
  DataTableSelectionCellContext,
  NewDataTableColumn,
  TableCellFactory,
} from '../data-table.types';
import { DataTableColumnResizeDirective } from '../directives/data-table-column-resize.directive';

// Temp
type ResolveRow<Col extends NewDataTableColumn<any> | DataTableColumn, Row extends DataTableRow> =
  Col extends NewDataTableColumn<infer R> ? (R extends DataTableRow ? R : Row) : Row;

@Component({
  selector: 'rt-data-table',
  styleUrls: ['./data-table.component.less'],
  template: `
    <rt-data-table-geometry
      [autoFit]="autoFit"
      [cols]="cols"
      (geometryChange)="geometryChange.emit($event)"
    >
      <table>
        <thead>
          <tr>
            @if (selectable) {
              <th
                [style.width]="selectableColumnWidth + 'px'"
                class="no-border selectable"
                [attr.data-cy]="'datatable:row:0:cell:select'"
              ></th>
            }
            @for (
              col of cols;
              track trackCol(index, col);
              let last = $last;
              let first = $first;
              let index = $index
            ) {
              <th
                [class.is-sortable]="col.options.sortable"
                [class.sort-asc]="col.sort === 'asc'"
                [class.sort-desc]="col.sort === 'desc'"
                [class.has-label]="!!col.label"
                [class.border]="!last"
                [class.icon-shift]="selectable && index === 0"
                [rtColumnResize]="col.id"
                [attr.data-cy]="'datatable:row:0:cell:' + col.id"
                (click)="col.options.sortable && sortChange.emit(col)"
              >
                <div
                  class="cell-body"
                  [style.left]="selectable && first ? -selectableColumnWidth + 'px' : 'unset'"
                >
                  <ng-container
                    *ngTemplateOutlet="
                      columnHeaderTemplate || defaultColumnHeaderTemplate;
                      context: columnHeaderTemplateContexts[col.id]
                    "
                  >
                  </ng-container>

                  <div class="sorting-icon-container">
                    <div class="fade"></div>
                    <rt-icon
                      [size]="'small-x'"
                      [name]="col.sort === 'asc' ? 'sort-ascending' : 'sort-descending'"
                      text-light
                    ></rt-icon>
                  </div>
                </div>
              </th>
            }
            @if (rowDetailsTemplate) {
              <th [style.width]="'50px'" class="no-border"></th>
            }
          </tr>
        </thead>
        <tbody>
          @for (row of rows; track trackByRow(index, row); let index = $index; let even = $even) {
            <rt-data-table-row
              [index]="index"
              [alwaysVisible]="index < rowsToPaintAtStart"
              [hoverable]="hoverable"
              [private]="!!row.private"
              [disabled]="!!row.disabled"
              [cellPadding]="cellPadding"
              [cellTemplates]="cellTemplates"
              [cellFactories]="cellFactories"
              [defaultCellTemplate]="defaultCellTemplate || dtDefaultCellTemplate"
              [selectable]="selectable"
              [clickable]="
                clickable === true || (clickable && clickable[row.id]) || !!rowDetailsTemplate
              "
              [selectionCellTemplate]="selectionCellTemplate"
              [row]="row"
              [columnIds]="columnIds"
              [selected]="selectable && selectedIdsDict[row.id] === true"
              [even]="even"
              [isZebra]="hasZebra"
              [rowDetailsTemplate]="rowDetailsTemplate"
              (click)="clickable && !row.disabled ? rowClick.emit(row) : null"
              (rowSelect)="selectable ? rowSelect.emit({ row: row, selected: $event }) : null"
            >
            </rt-data-table-row>
          }
        </tbody>
      </table>
    </rt-data-table-geometry>

    <ng-template #dtDefaultCellTemplate let-row="row" let-columnId="columnId">
      {{ row[columnId] }}
    </ng-template>
    <ng-template #defaultColumnHeaderTemplate let-column="col"> {{ column.label }} </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.clickable]': 'clickable',
    '[class.selectable]': 'selectable',
    '[class.raised]': 'raised',
    '[class.has-smaller-padding]': 'hasSmallerPadding',
  },
  imports: [
    DataTableColumnResizeDirective,
    DataTableGeometryComponent,
    DataTableRowComponent,
    IconComponent,
    NgTemplateOutlet,
  ],
})
export class DataTableComponent<
  Col extends NewDataTableColumn<Row> | DataTableColumn,
  Row extends DataTableRow,
> {
  @Input() public cols: Col[];
  @Input() public rows: Array<ResolveRow<Col, Row>>;

  /**
   * @deprecated Use `column.cellFactory` instead
   */
  @Input() public cellTemplates?: { [key: string]: TemplateRef<DataTableCellContext> };
  @Input() public rowDetailsTemplate: TemplateRef<any>;
  /**
   * @deprecated Use `cellFactory: textCellFactory('propName')` instead
   */
  @Input() public defaultCellTemplate?: TemplateRef<DataTableCellContext>;
  @Input() public selectionCellTemplate?: TemplateRef<DataTableSelectionCellContext>;
  @Input() public columnHeaderTemplate?: TemplateRef<{ col: Col }>;

  @Input() public autoFit: boolean = true;

  @Input() public cellPadding: boolean = true;

  @Input() public raised: boolean = true;
  @Input() public rowsToPaintAtStart: number = Infinity;
  @Input() public lazyPaint: boolean = false;

  @Input() public clickable: boolean | Record<string, boolean> = false;
  @Input() public hoverable: boolean = false;
  @Input() public selectable: boolean = false;
  @Input() public selectedIdsDict: Record<number | string, boolean> = {};
  @Input() public selectableColumnWidth: number = 32;
  @Input() public hasZebra: boolean = true;

  @Input() public hasSmallerPadding: boolean = false;

  public sortChange = output<Col>();
  public rowClick = output<ResolveRow<Col, Row>>();
  public rowSelect = output<{
    row: ResolveRow<Col, Row>;
    selected: boolean;
  }>();
  public geometryChange = output<DataTableColumnGeometryChange>();

  public columnIds: string[] = [];
  public cellFactories: Record<string, TableCellFactory<ResolveRow<Col, Row>> | null>;
  public columnHeaderTemplateContexts: { [key: string]: { col: Col } } = {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.cols) {
      const columnIdsChanged =
        changes.cols.firstChange ||
        changes.cols.previousValue.map((col: Col) => col.id).join('#') !==
          changes.cols.currentValue.map((col: Col) => col.id).join('#');
      if (columnIdsChanged) {
        this.columnIds = this.cols.map(col => col.id);
      }

      this.cellFactories = this.cols.reduce(
        (acc, col) => {
          acc[col.id] = 'componentFactory' in col ? col.componentFactory : null;
          return acc;
        },
        <Record<string, TableCellFactory<DataTableRow> | null>>{},
      );
      this.columnHeaderTemplateContexts = this.cols.reduce(
        (acc, col) => ({ ...acc, [col.id]: { col } }),
        {},
      );
    }
  }

  public trackCol<C extends DataTableColumn>(index: number, entity: C): string {
    return entity.id;
  }

  public trackByRow<R extends DataTableRow>(index: number, row: R): R['id'] {
    return row.id;
  }
}
